/**
 * DebugScene - Overlay debug menu for toggling upgrades
 * Activated by pressing backtick (`) key
 */

import Phaser from 'phaser';
import { GameStateManager } from '../game/GameStateManager';
import { GAME_CONFIG, Z_DEPTHS, SCENES } from '../constants/GameConstants';
import {
  UPGRADE_LIBRARY,
  Upgrade,
  getUpgradeColor,
  convertToPieceUpgrade,
} from '../game/upgrades/UpgradeLibrary';
import { AIConfig } from '../types';

const ROW_HEIGHT = 44;
const VISIBLE_AREA_TOP = 118;
const VISIBLE_AREA_BOTTOM = GAME_CONFIG.HEIGHT - 40;
const VISIBLE_HEIGHT = VISIBLE_AREA_BOTTOM - VISIBLE_AREA_TOP;

type DifficultyPreset = { label: string; config: AIConfig | null; color: string };

const DIFFICULTY_PRESETS: DifficultyPreset[] = [
  { label: 'DEFAULT', config: null, color: '#ffffff' },
  { label: 'BEGINNER', config: { depth: 2, moveTime: 100, difficulty: 'beginner' }, color: '#4caf50' },
  { label: 'EASY', config: { depth: 3, moveTime: 150, difficulty: 'easy' }, color: '#8bc34a' },
  { label: 'MEDIUM', config: { depth: 4, moveTime: 250, difficulty: 'medium' }, color: '#ffc107' },
  { label: 'HARD', config: { depth: 6, moveTime: 500, difficulty: 'hard' }, color: '#ff9800' },
  { label: 'EXPERT', config: { depth: 8, moveTime: 2000, difficulty: 'expert' }, color: '#f44336' },
];

export class DebugScene extends Phaser.Scene {
  private gameStateManager!: GameStateManager;
  private scrollContainer!: Phaser.GameObjects.Container;
  private toggleIndicators: Map<string, Phaser.GameObjects.Text> = new Map();
  private scrollY = 0;
  private maxScroll = 0;
  private allUpgrades: Upgrade[] = [];
  private filterRarity: string | null = null;
  private filterButtons: Phaser.GameObjects.Text[] = [];
  private difficultyButtons: Phaser.GameObjects.Text[] = [];

  constructor() {
    super(SCENES.DEBUG);
  }

  create(): void {
    this.gameStateManager = GameStateManager.getInstance();
    this.allUpgrades = Object.values(UPGRADE_LIBRARY);

    // Sort: legendary, rare, uncommon, common
    const rarityOrder: Record<string, number> = { legendary: 0, rare: 1, uncommon: 2, common: 3 };
    this.allUpgrades.sort((a, b) => rarityOrder[a.rarity] - rarityOrder[b.rarity]);

    // Semi-transparent overlay
    const overlay = this.add.rectangle(
      GAME_CONFIG.WIDTH / 2, GAME_CONFIG.HEIGHT / 2,
      GAME_CONFIG.WIDTH, GAME_CONFIG.HEIGHT,
      0x000000, 0.85
    );
    overlay.setDepth(Z_DEPTHS.MODAL);
    overlay.setInteractive(); // Block clicks through to scene below

    // Title
    this.add.text(GAME_CONFIG.WIDTH / 2, 14, 'DEBUG MENU', {
      fontSize: '22px',
      fontFamily: 'monospace',
      color: '#ff4444',
      fontStyle: 'bold',
    }).setOrigin(0.5, 0).setDepth(Z_DEPTHS.MODAL + 2);

    // AI difficulty picker
    this.createDifficultyBar();

    // Filter buttons
    this.createFilterBar();

    // Close hint
    this.add.text(GAME_CONFIG.WIDTH / 2, GAME_CONFIG.HEIGHT - 18, 'Press ` to close', {
      fontSize: '14px',
      fontFamily: 'monospace',
      color: '#888888',
    }).setOrigin(0.5, 0.5).setDepth(Z_DEPTHS.MODAL + 2);

    // Scrollable container
    this.scrollContainer = this.add.container(0, VISIBLE_AREA_TOP);
    this.scrollContainer.setDepth(Z_DEPTHS.MODAL + 1);

    // Create mask for scroll area
    const maskShape = this.make.graphics({ x: 0, y: 0 });
    maskShape.fillRect(0, VISIBLE_AREA_TOP, GAME_CONFIG.WIDTH, VISIBLE_HEIGHT);
    const mask = maskShape.createGeometryMask();
    this.scrollContainer.setMask(mask);

    this.buildUpgradeList();

    // Scroll input
    this.input.on('wheel', (_pointer: any, _gameObjects: any, _deltaX: number, deltaY: number) => {
      this.scrollY = Phaser.Math.Clamp(this.scrollY - deltaY, -this.maxScroll, 0);
      this.scrollContainer.y = VISIBLE_AREA_TOP + this.scrollY;
    });

    // Touch/drag scrolling
    let dragStartY = 0;
    let dragStartScroll = 0;
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      dragStartY = pointer.y;
      dragStartScroll = this.scrollY;
    });
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!pointer.isDown) return;
      const dy = pointer.y - dragStartY;
      if (Math.abs(dy) > 5) {
        this.scrollY = Phaser.Math.Clamp(dragStartScroll + dy, -this.maxScroll, 0);
        this.scrollContainer.y = VISIBLE_AREA_TOP + this.scrollY;
      }
    });

    // Backtick to close
    this.input.keyboard?.on('keydown-BACKTICK', () => {
      this.closeDebugMenu();
    });

    // Escape to close too
    this.input.keyboard?.on('keydown-ESC', () => {
      this.closeDebugMenu();
    });
  }

  private createDifficultyBar(): void {
    this.add.text(30, 46, 'AI:', {
      fontSize: '13px',
      fontFamily: 'monospace',
      color: '#888888',
    }).setOrigin(0, 0).setDepth(Z_DEPTHS.MODAL + 2);

    const current = this.gameStateManager.getAIConfigOverride();
    const activeIdx = current === null
      ? 0
      : DIFFICULTY_PRESETS.findIndex(p => p.config?.difficulty === current.difficulty);

    const startX = 64;
    const y = 42;
    const spacing = 108;

    DIFFICULTY_PRESETS.forEach((preset, i) => {
      const isActive = i === activeIdx;
      const btn = this.add.text(startX + i * spacing, y, preset.label, {
        fontSize: '13px',
        fontFamily: 'monospace',
        color: preset.color,
        fontStyle: isActive ? 'bold' : 'normal',
        backgroundColor: isActive ? '#333333' : undefined,
        padding: { x: 6, y: 3 },
      }).setDepth(Z_DEPTHS.MODAL + 2).setInteractive({ useHandCursor: true });

      btn.on('pointerdown', () => {
        this.gameStateManager.setAIConfigOverride(preset.config);
        this.refreshDifficultyBar();
      });

      this.difficultyButtons.push(btn);
    });
  }

  private refreshDifficultyBar(): void {
    const current = this.gameStateManager.getAIConfigOverride();
    const activeIdx = current === null
      ? 0
      : DIFFICULTY_PRESETS.findIndex(p => p.config?.difficulty === current.difficulty);

    this.difficultyButtons.forEach((btn, i) => {
      const preset = DIFFICULTY_PRESETS[i];
      const isActive = i === activeIdx;
      btn.setStyle({
        fontSize: '13px',
        fontFamily: 'monospace',
        color: preset.color,
        fontStyle: isActive ? 'bold' : 'normal',
        backgroundColor: isActive ? '#333333' : undefined,
        padding: { x: 6, y: 3 },
      });
    });
  }

  private createFilterBar(): void {
    const rarities = ['all', 'legendary', 'rare', 'uncommon', 'common'];
    const colors: Record<string, string> = {
      all: '#ffffff',
      legendary: '#ff9800',
      rare: '#2196f3',
      uncommon: '#4caf50',
      common: '#9e9e9e',
    };

    const startX = 30;
    const y = 84;

    rarities.forEach((r, i) => {
      const btn = this.add.text(startX + i * 130, y, r.toUpperCase(), {
        fontSize: '14px',
        fontFamily: 'monospace',
        color: colors[r],
        fontStyle: this.filterRarity === (r === 'all' ? null : r) ? 'bold' : 'normal',
        backgroundColor: r === 'all' && this.filterRarity === null ? '#333333' : undefined,
        padding: { x: 6, y: 3 },
      }).setDepth(Z_DEPTHS.MODAL + 2).setInteractive({ useHandCursor: true });

      btn.on('pointerdown', () => {
        this.filterRarity = r === 'all' ? null : r;
        this.scrollY = 0;
        this.scrollContainer.y = VISIBLE_AREA_TOP;
        this.rebuildList();
      });

      this.filterButtons.push(btn);
    });
  }

  private rebuildList(): void {
    // Update filter button styles
    const rarities = ['all', 'legendary', 'rare', 'uncommon', 'common'];
    const colors: Record<string, string> = {
      all: '#ffffff',
      legendary: '#ff9800',
      rare: '#2196f3',
      uncommon: '#4caf50',
      common: '#9e9e9e',
    };

    this.filterButtons.forEach((btn, i) => {
      const r = rarities[i];
      const isActive = this.filterRarity === (r === 'all' ? null : r);
      btn.setStyle({
        fontSize: '14px',
        fontFamily: 'monospace',
        color: colors[r],
        fontStyle: isActive ? 'bold' : 'normal',
        backgroundColor: isActive ? '#333333' : undefined,
        padding: { x: 6, y: 3 },
      });
    });

    this.scrollContainer.removeAll(true);
    this.toggleIndicators.clear();
    this.buildUpgradeList();
  }

  private buildUpgradeList(): void {
    const activeUpgrades = this.gameStateManager.getActiveUpgrades();
    const activeIds = new Set(activeUpgrades.map(u => u.id));

    let filtered = this.allUpgrades;
    if (this.filterRarity) {
      filtered = filtered.filter(u => u.rarity === this.filterRarity);
    }

    filtered.forEach((upgrade, i) => {
      const y = i * ROW_HEIGHT;
      const isActive = activeIds.has(upgrade.id);

      // Row background
      const bg = this.add.rectangle(
        GAME_CONFIG.WIDTH / 2, y + ROW_HEIGHT / 2,
        GAME_CONFIG.WIDTH - 20, ROW_HEIGHT - 4,
        isActive ? 0x2a3a2a : 0x1a1a2e, 0.8
      ).setStrokeStyle(1, isActive ? 0x4caf50 : 0x333355);
      bg.setInteractive({ useHandCursor: true });

      // Toggle indicator
      const toggleText = this.add.text(20, y + 6, isActive ? 'ON' : 'OFF', {
        fontSize: '14px',
        fontFamily: 'monospace',
        fontStyle: 'bold',
        color: isActive ? '#4caf50' : '#666666',
      });
      this.toggleIndicators.set(upgrade.id, toggleText);

      // Rarity dot
      const rarityColor = getUpgradeColor(upgrade.rarity);
      const rarityDot = this.add.text(58, y + 6, '\u25cf', {
        fontSize: '14px',
        color: rarityColor,
      });

      // Upgrade name
      const nameText = this.add.text(76, y + 4, upgrade.name, {
        fontSize: '15px',
        fontFamily: 'monospace',
        color: '#ffffff',
        fontStyle: 'bold',
      });

      // Description
      const descText = this.add.text(76, y + 22, upgrade.description, {
        fontSize: '11px',
        fontFamily: 'monospace',
        color: '#aaaaaa',
        wordWrap: { width: GAME_CONFIG.WIDTH - 100 },
      });

      // Click handler
      bg.on('pointerdown', (_pointer: Phaser.Input.Pointer) => {
        this.toggleUpgrade(upgrade);
      });

      // Hover
      bg.on('pointerover', () => {
        bg.setFillStyle(isActive ? 0x3a4a3a : 0x2a2a3e, 0.9);
      });
      bg.on('pointerout', () => {
        bg.setFillStyle(isActive ? 0x2a3a2a : 0x1a1a2e, 0.8);
      });

      this.scrollContainer.add([bg, toggleText, rarityDot, nameText, descText]);
    });

    const totalHeight = filtered.length * ROW_HEIGHT;
    this.maxScroll = Math.max(0, totalHeight - VISIBLE_HEIGHT);
  }

  private toggleUpgrade(upgrade: Upgrade): void {
    const activeUpgrades = this.gameStateManager.getActiveUpgrades();
    const isActive = activeUpgrades.some(u => u.id === upgrade.id);

    if (isActive) {
      this.gameStateManager.removeActiveUpgrade(upgrade.id);
    } else {
      this.gameStateManager.addActiveUpgrade(convertToPieceUpgrade(upgrade));
    }

    // Rebuild the list to update visual state
    this.scrollY = this.scrollContainer.y - VISIBLE_AREA_TOP;
    this.scrollContainer.removeAll(true);
    this.toggleIndicators.clear();
    this.buildUpgradeList();
    this.scrollContainer.y = VISIBLE_AREA_TOP + this.scrollY;
  }

  private closeDebugMenu(): void {
    this.scene.stop(SCENES.DEBUG);
  }
}
